import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationBell } from '@/components/NotificationBell';
import { SiteFooter } from '@/components/SiteFooter';
import { ConnectionBanner } from '@/lib/useConnection';
import { useSite } from '@/lib/useSite';
import {
  Check,
  ChevronDown,
  Landmark,
  LogOut,
  Menu,
  LifeBuoy,
  MoreHorizontal,
  UserRound,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { landingRoute, useAuth } from '@/auth/AuthContext';
import { useBuilding } from '@/auth/BuildingContext';
import { LANG_KEY } from '@/i18n';
import { initials } from '@/lib/format';
import { sanitizeSearch } from '@/lib/validation';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { useLocalizedName } from '@/lib/useLocalizedName';
import { bottomNavRoutes, navIcons, visibleNavItems } from '@/nav';
import { LogoMark } from './LogoMark';

const COLLAPSE_KEY = 'omaraty.sidebarCollapsed';
const ACTION_SLOT_ID = 'page-action-slot';

export function Brand({ tone = 'brand' }: { tone?: 'brand' | 'light' }) {
  const { t } = useTranslation();
  const { isAuthenticated, roles } = useAuth();
  const localized = useLocalizedName();
  const { settings } = useSite();

  // الاسم من إعدادات الموقع لا من ملف الترجمة: مالك المنصّة يغيّره من
  // `/admin/site` بلا نشر، وقراءته من مفتاح ترجمة كانت تعني أن الشعار
  // يبقى على الاسم القديم بينما يتغيّر في كل مكان آخر.
  const appName = localized(settings.appNameEn, settings.appNameAr) || t('app.name');
  const tagline = localized(settings.taglineEn, settings.taglineAr) || t('app.tagline');

  return (
    <Link to={isAuthenticated ? landingRoute(roles) : '/login'} className="brand">
      <span className={`brand-mark${tone === 'brand' ? ' on-light' : ''}`}>
        <LogoMark size={26} tone={tone} title={appName} />
      </span>
      <span>
        <strong style={tone === 'light' ? { color: '#fff' } : undefined}>{appName}</strong>
        <small>{tagline}</small>
      </span>
    </Link>
  );
}

export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const next = i18n.language === 'ar' ? 'en' : 'ar';

  return (
    <button
      type="button"
      className="lang-toggle"
      aria-label={t('app.languageLabel')}
      onClick={() => {
        void i18n.changeLanguage(next);
        localStorage.setItem(LANG_KEY, next);
      }}
    >
      {t('app.language')}
    </button>
  );
}

/* ------------------------------------------------------ building switcher */

function BuildingSwitcher({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const localized = useLocalizedName();
  const { buildings, activeBuilding, selectBuilding, canSwitch } = useBuilding();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const term = sanitizeSearch(query).toLowerCase();
  const filtered = buildings.filter((building) =>
    term === ''
      ? true
      : `${building.nameEn} ${building.nameAr} ${building.areaEn} ${building.areaAr}`
          .toLowerCase()
          .includes(term),
  );

  const label = activeBuilding
    ? localized(activeBuilding.nameEn, activeBuilding.nameAr)
    : t('common.loading');
  const sub = activeBuilding
    ? localized(activeBuilding.areaEn, activeBuilding.areaAr)
    : '';

  return (
    <div className="building-menu" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={`building-switcher${canSwitch ? ' switchable' : ''}`}
        onClick={() => canSwitch && setOpen((value) => !value)}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? open : undefined}
        aria-label={canSwitch ? t('building.switch') : label}
        disabled={!canSwitch}
      >
        <span className="building-avatar">
          {activeBuilding ? initials(localized(activeBuilding.nameEn, activeBuilding.nameAr)) : <Landmark size={16} />}
        </span>
        <span>
          <b>{label}</b>
          <small>{sub}</small>
        </span>
        {canSwitch && <ChevronDown size={15} className="sidebar-chevron" />}
      </button>

      {open && !collapsed && (
        <div className="building-menu-panel" role="listbox">
          <div className="search-field">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('building.searchPlaceholder')}
              aria-label={t('common.search')}
              maxLength={60}
              autoFocus
            />
          </div>
          <div className="building-menu-list">
            {filtered.map((building) => (
              <button
                key={building.id}
                type="button"
                role="option"
                aria-selected={building.id === activeBuilding?.id}
                className={building.id === activeBuilding?.id ? 'selected' : undefined}
                onClick={() => {
                  selectBuilding(building.id);
                  setOpen(false);
                  setQuery('');
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>{localized(building.nameEn, building.nameAr)}</b>
                  <small>
                    {localized(building.areaEn, building.areaAr)} · {building.unitCount}{' '}
                    {t('unit.totalUnits')}
                  </small>
                </span>
                {building.id === activeBuilding?.id && <Check size={14} />}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="building-menu-empty">{t('common.noResults')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ nav */

function Sidebar({
  collapsed,
  onToggleCollapse,
  onClose,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { roles, userName, isAdmin, isManager, buildingPublicId } = useAuth();
  const { activeBuilding } = useBuilding();
  // المنصّة تتنقّل بين العمارات؛ المسؤول والساكن على عمارتهم من الجلسة.
  const activePublicId = activeBuilding?.publicId ?? buildingPublicId ?? null;

  const items = visibleNavItems(roles);
  const platform = items.filter((item) => item.section === 'platform');
  const main = items.filter((item) => item.section === undefined || item.section === 'main');
  const comms = items.filter((item) => item.section === 'comms');

  const roleLabel = isAdmin ? t('user.admin') : isManager ? t('user.manager') : t('user.resident');

  const renderLink = (to: string, labelKey: string, iconName: keyof typeof navIcons) => {
    const Icon = navIcons[iconName];
    // Exact match for /admin so it doesn't stay lit on /admin/users.
    const active = to === '/admin' ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
    return (
      <NavLink
        key={to}
        to={to}
        className={`side-link${active ? ' active' : ''}`}
        title={collapsed ? t(labelKey) : undefined}
        onClick={onClose}
      >
        <Icon size={18} />
        <span>{t(labelKey)}</span>
      </NavLink>
    );
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <Brand tone="light" />
        {onClose && (
          <button
            type="button"
            className="icon-button light mobile-only"
            onClick={onClose}
            aria-label={t('app.closeMenu')}
          >
            <X size={18} />
          </button>
        )}
        <button
          type="button"
          className="icon-button light desktop-only"
          onClick={onToggleCollapse}
          aria-label={collapsed ? t('app.expand') : t('app.collapse')}
        >
          {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
      </div>

      <BuildingSwitcher collapsed={collapsed} />

      <nav className="side-nav" aria-label={t('app.menu')}>
        {platform.length > 0 && (
          <>
            <small className="nav-caption">{t('app.platformSpace')}</small>
            {platform.map((item) => renderLink(item.to, item.labelKey, item.icon))}
            <small className="nav-caption second">{t('app.buildingSpace')}</small>
          </>
        )}
        {platform.length === 0 && (
          <small className="nav-caption">
            {isManager ? t('app.managerSpace') : t('app.residentSpace')}
          </small>
        )}
        {main.map((item) => renderLink(item.to, item.labelKey, item.icon))}
        {comms.length > 0 && <small className="nav-caption second">{t('app.publicSpace')}</small>}
        {comms.map((item) => renderLink(item.to, item.labelKey, item.icon))}
      </nav>

      <div className="sidebar-bottom">
        {/* #102: البطاقة رابط إلى شفافية العمارة النشطة — كانت نصّاً لا يُضغط. */}
        {activePublicId ? (
          <Link to={`/t/${activePublicId}`} className="trust-note" onClick={onClose}>
            <ShieldCheck size={16} />
            <span>
              <b>{t('app.trustTitle')}</b>
              <small>{t('app.trustBody')}</small>
            </span>
          </Link>
        ) : (
          <div className="trust-note">
            <ShieldCheck size={16} />
            <span>
              <b>{t('app.trustTitle')}</b>
              <small>{t('app.trustBody')}</small>
            </span>
          </div>
        )}
        <ProfileMini userName={userName} roleLabel={roleLabel} onNavigate={onClose} />
      </div>
    </aside>
  );
}

/**
 * The three dots at the bottom of the sidebar.
 *
 * It rendered a `MoreHorizontal` icon with no click handler at all — it
 * looked interactive and did nothing. Now it opens the same menu as the
 * header avatar, so there is one set of personal actions rather than two
 * places that disagree.
 */
function ProfileMini({
  userName,
  roleLabel,
  onNavigate,
}: {
  userName: string | null;
  roleLabel: string;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="profile-mini-wrap" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="profile-mini"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar">{initials(userName ?? '—')}</span>
        <span>
          <b>{userName}</b>
          <small>{roleLabel}</small>
        </span>
        <MoreHorizontal size={17} aria-hidden />
      </button>

      {open && (
        <div className="user-menu-panel user-menu-panel--up" role="menu">
          <UserMenuItems
            onNavigate={() => {
              setOpen(false);
              onNavigate?.();
            }}
          />
        </div>
      )}
    </div>
  );
}

/** The personal actions themselves, shared by both entry points. */
function UserMenuItems({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onNavigate?.();
          navigate('/account');
        }}
      >
        <UserRound size={15} aria-hidden />
        {t('account.title')}
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onNavigate?.();
          navigate('/support');
        }}
      >
        <LifeBuoy size={15} aria-hidden />
        {t('footer.supportCentre')}
      </button>
      <button type="button" role="menuitem" className="danger" onClick={logout}>
        <LogOut size={15} aria-hidden />
        {t('app.logout')}
      </button>
    </>
  );
}

/**
 * مُصدَّر (#53): صفحة الساكن وصفحة الشفافية خارج `Layout`، فكان الساكن بلا
 * مخرج — لا حساب ولا دعم ولا تسجيل خروج من صفحته الرئيسية. قائمة واحدة
 * في المكانين، لا نسختان تنحرفان.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const { userName, isAdmin, isManager } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const roleLabel = isAdmin ? t('user.admin') : isManager ? t('user.manager') : t('user.resident');

  return (
    <div className="user-menu" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="avatar"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={userName ?? t('user.resident')}
      >
        {initials(userName ?? '—')}
      </button>
      {open && (
        <div className="user-menu-panel" role="menu">
          <header>
            <b>{userName}</b>
            <small>{roleLabel}</small>
          </header>
          <UserMenuItems onNavigate={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

export function AppHeader({
  title,
  eyebrow,
  onMenu,
}: {
  title: string;
  eyebrow?: string;
  onMenu: () => void;
}) {
  const { t } = useTranslation();

  return (
    <header className="topbar">
      <button
        type="button"
        className="icon-button mobile-only menu-trigger"
        onClick={onMenu}
        aria-label={t('app.menu')}
      >
        <Menu size={20} />
      </button>
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
      </div>
      <div className="top-actions">
        <NotificationBell />
        <LanguageToggle />
        <UserMenu />
        <span id={ACTION_SLOT_ID} className="action-slot" />
      </div>
    </header>
  );
}

function BottomNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { roles } = useAuth();

  const items = bottomNavRoutes.filter(
    (item) => !item.roles || item.roles.some((role) => roles.includes(role)),
  );

  return (
    <nav className="mobile-tabs mobile-only" aria-label={t('app.menu')}>
      {items.map((item) => {
        const Icon = navIcons[item.icon];
        const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
        return (
          <NavLink key={item.to} to={item.to} className={active ? 'active' : undefined}>
            <Icon size={20} />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------- page head */

type PageHeaderData = { title: string; eyebrow?: string };

const HeaderContext = createContext<((header: PageHeaderData) => void) | null>(null);

/**
 * Pages publish their title into the fixed header.
 * Only strings cross the boundary, so this never re-triggers on every render.
 */
export function usePageHeader(title: string, eyebrow?: string) {
  const setHeader = useContext(HeaderContext);
  useEffect(() => {
    setHeader?.({ title, eyebrow });
  }, [setHeader, title, eyebrow]);
}

/** Renders a page's primary action into the header's action slot. */
export function PageAction({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSlot(document.getElementById(ACTION_SLOT_ID));
  }, []);

  return slot ? createPortal(children, slot) : null;
}

export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true');
  const [header, setHeader] = useState<PageHeaderData>({ title: '' });
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const toggleCollapse = () => {
    setCollapsed((value) => {
      localStorage.setItem(COLLAPSE_KEY, String(!value));
      return !value;
    });
  };

  const publish = useMemo(() => (next: PageHeaderData) => setHeader(next), []);

  return (
    <HeaderContext.Provider value={publish}>
      <div className="app-shell noise">
        <div
          className={`sidebar-wrap${menuOpen ? ' open' : ''}${
            collapsed && isDesktop ? ' collapsed' : ''
          }`}
          onClick={(event) => {
            if (event.target === event.currentTarget) setMenuOpen(false);
          }}
        >
          <Sidebar
            collapsed={collapsed && isDesktop}
            onToggleCollapse={toggleCollapse}
            onClose={() => setMenuOpen(false)}
          />
        </div>
        <main className="main-area">
          <AppHeader title={header.title} eyebrow={header.eyebrow} onMenu={() => setMenuOpen(true)} />
          <ConnectionBanner />
          <div className="page-content page-in mobile-bottom-space" key={pathname}>
            <Outlet />
            <SiteFooter />
          </div>
        </main>
        <BottomNav />
      </div>
    </HeaderContext.Provider>
  );
}
